import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'hooks/use-search-params.hook';
import qs from 'qs';
import { replace } from 'store/router/actions';
import { DEFAULT_TYPE_COMMA_DELIMITER, DEFAULT_TYPE_DELIMITER } from 'shared/consts';
import { useGetResourceCollectionsQuery } from 'store/collections/hooks';
import { ResourceCollection } from 'store/collections/types';

type ExtendedResourceCollection = ResourceCollection & { originalUuids?: string[] };

const getSelectedCollections = (filterByCollection: string, collections: ExtendedResourceCollection[]): string[] => {
  if (!filterByCollection || !collections?.length) {
    return [];
  }

  const collectionUuids = filterByCollection
    .replace(new RegExp(DEFAULT_TYPE_COMMA_DELIMITER, 'g'), ',')
    .split(',')
    .filter(Boolean);

  const expandedUuids: string[] = [];

  collectionUuids.forEach(uuid => {
    const collection = collections.find(
      currentCollection =>
        currentCollection.uuid === uuid ||
        (currentCollection.originalUuids && currentCollection.originalUuids.includes(uuid)),
    );

    if (collection?.originalUuids) {
      expandedUuids.push(...collection.originalUuids);
    } else {
      expandedUuids.push(uuid);
    }
  });

  return [...new Set(expandedUuids)];
};

interface UseCollectionsFilterReturnValues {
  selectedCollections: string[];
  onCollectionChipClickHandler: (value: string) => void;
}

export const useCollectionsFilter = (): UseCollectionsFilterReturnValues => {
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const { filterByCollection, ...restQueryParams } = useSearchParams();
  const dispatch = useDispatch();
  const { data: collections } = useGetResourceCollectionsQuery() as { data: ExtendedResourceCollection[] };

  useEffect(() => {
    const filterByCollectionToString: string = filterByCollection
      ? filterByCollection.toString().replace(new RegExp(DEFAULT_TYPE_DELIMITER, 'g'), '')
      : '';
    const selected = getSelectedCollections(filterByCollectionToString, collections);
    setSelectedCollections(filterByCollection?.length ? [...new Set(selected)] : []);
  }, [filterByCollection, collections]);

  const onCollectionChipClickHandler = useCallback(
    (value: string) => {
      const collection = collections?.find(currentCollection => currentCollection.uuid === value);
      const uuidsToToggle = collection?.originalUuids || [value];

      const isAnySelected = uuidsToToggle.some((uuid: string) => selectedCollections.includes(uuid));

      let queryString: string;
      if (isAnySelected) {
        let updatedSelected = selectedCollections;
        uuidsToToggle.forEach((uuid: string) => {
          updatedSelected = updatedSelected.filter(selectedUuid => selectedUuid !== uuid);
        });
        queryString =
          updatedSelected.length > 0
            ? qs.stringify({
                ...restQueryParams,
                filterByCollection: updatedSelected
                  .map(uuid => uuid.replace(',', DEFAULT_TYPE_COMMA_DELIMITER))
                  .join(','),
              })
            : qs.stringify(restQueryParams);
      } else {
        const updatedSelected = [...selectedCollections, ...uuidsToToggle];
        queryString = qs.stringify({
          ...restQueryParams,
          filterByCollection: updatedSelected.map(uuid => uuid.replace(',', DEFAULT_TYPE_COMMA_DELIMITER)).join(','),
        });
      }

      dispatch(replace(`${location.pathname}?${queryString}`));
    },
    [selectedCollections, restQueryParams, collections, dispatch],
  );

  return { selectedCollections, onCollectionChipClickHandler };
};
